import { getIntervalDifferenceLabel } from '@utils/granularity';
import moment from 'moment-timezone';
import getHourlyIntervalFormat from '@utils/getHourlyIntervalFormat';
import type { AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';

export interface Interval {
  from: string;
  to: string;
}

export const DIMENSION_WRAPPER_ID = 'dimension-panel-wrapper';
export const TABLE_COMPARE_COLOR = 'rgba(60, 60, 60, 0.2)';
export const TABLE_COMPARE_OPACITY = '4D';

export const getCompareIntervalLabel = ({ isSliceAvailable, dodEnabled, compareInterval }) => {
  if (isSliceAvailable) {
    return `Compare last ${getIntervalDifferenceLabel(compareInterval)}`;
  } else if (dodEnabled) {
    return 'Date Comparison';
  }
  return 'Table Compare';
};

export const getCompareTooltipText = ({
  isCompareToggleEnabled,
  isValidCompareInterval,
  compareInterval,
}) => {
  switch (true) {
    case !isCompareToggleEnabled:
      return 'Turn on Date Comparison or Slice the metric chart to enable Table Compare.';

    case !isValidCompareInterval:
      return 'Comparison unavailable';

    default:
      return formatInterval(compareInterval, false);
  }
};

const format = {
  short: 'HH:mm',
  long: 'MMM DD YYYY HH:mm',
  withSeconds: 'HH:mm:ss',
};

// eslint-disable-next-line import/prefer-default-export
export const formatInterval = (interval: Interval, liveMode: boolean): string => {
  const { from, to } = interval;
  let startInterval = moment(from);
  let endInterval = moment(to);
  if (moment().diff(startInterval, 'days') > 31) {
    const { start, end } = getHourlyIntervalFormat(startInterval, endInterval);
    startInterval = start;
    endInterval = end;
  }

  if (liveMode) {
    return `${startInterval.format(format.withSeconds)} - ${endInterval.format(format.withSeconds)}`;
  }

  if (startInterval.isSame(endInterval, 'day')) {
    return `${startInterval.format(format.long)} - ${endInterval.format(format.short)}`;
  }
  return `${startInterval.format(format.long)} - ${endInterval.format(format.long)}`;
};

export function checkFlowNWRRCAAvaliable(flowConifg: AppFlowMetadata) {
  return !flowConifg.metaDefinition.metrics.find(
    (it) => it.scope === 'service' && it.type?.includes('network_request'),
  );
}

/**
 * Whether the Sessions tab should render — the feature/account gate only
 * (`showViewersTab && !isCDNAccount`). The per-user `hasViewerAccess` permission NO LONGER hides
 * the tab: the tab stays visible and its content is gated by ViewerAccessGate, which shows a
 * "no permission" message when the user lacks access (PDF-1524). Kept as a pure function for tests.
 */
export function shouldShowSessionsTab(params: {
  showViewersTab: boolean;
  isCDNAccount: boolean;
}): boolean {
  const { showViewersTab, isCDNAccount } = params;
  return Boolean(showViewersTab) && !isCDNAccount;
}
